import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import {
  Train, Users, MapPin, Menu, X, Ticket, Shield, LogOut, User, Building2, Zap, Globe, Route as RouteIcon, DollarSign, RefreshCw, ScanLine
} from 'lucide-react';
import StationsManager from './components/StationsManager';
import TicketsManager from './components/TicketsManager';
import UsersManager from './components/UsersManager';
import RolesManager from './components/RolesManager';
import CompanyManager from './components/CompanyManager';
import LinesManager from './components/LinesManager';
import RegionManager from './components/RegionManager';
import RouteManagementManager from './components/RouteManagementManager';
import PricingManagement from './components/PricingManagement';
import RouteUpdateManager from './components/RouteUpdateManager';
import TicketValidationManager from './components/TicketValidationManager';
import Login from './components/Login';
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Sidebar Component
const Sidebar: React.FC<{ isOpen: boolean; onClose: () => void }> = ({ isOpen, onClose }) => {
  const location = useLocation();
  const { user, logout } = useAuth();

  const menuItems = [
    { path: '/regions', icon: Globe, label: 'Regions' },
    { path: '/companies', icon: Building2, label: 'Companies' },
    { path: '/lines', icon: Zap, label: 'Train Lines' },
    { path: '/', icon: MapPin, label: 'Stations' },
    { path: '/route-management', icon: RouteIcon, label: 'Route Management' },
    { path: '/pricing', icon: DollarSign, label: 'Pricing Management' },
    { path: '/route-updates', icon: RefreshCw, label: 'Update Routes Cache' },
    { path: '/tickets', icon: Ticket, label: 'Tickets' },
    { path: '/validations', icon: ScanLine, label: 'Ticket Validations' },
    { path: '/users', icon: Users, label: 'Users' },
    { path: '/roles', icon: Shield, label: 'Roles' },
  ];

  return (
    <div className={`fixed inset-y-0 left-0 z-50 w-64 bg-admin-800 transform ${isOpen ? 'translate-x-0' : '-translate-x-full'} transition-transform duration-200 ease-in-out lg:translate-x-0 lg:static lg:inset-0 flex flex-col`}>
      {/* Fixed Header */}
      <div className="flex items-center justify-between h-16 px-6 bg-admin-900 flex-shrink-0">
        <div className="flex items-center space-x-2">
          <Train className="h-8 w-8 text-white" />
          <span className="text-xl font-bold text-white">Admin Panel</span>
        </div>
        <button
          onClick={onClose}
          className="lg:hidden text-white hover:text-gray-200"
        >
          <X className="h-6 w-6" />
        </button>
      </div>

      {/* Fixed User Info */}
      {user && (
        <div className="px-6 py-4 border-b border-admin-700 flex-shrink-0">
          <div className="flex items-center space-x-3">
            <User className="h-8 w-8 text-admin-300" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">
                {user.name}
              </p>
              <p className="text-xs text-admin-300 truncate">{user.email}</p>
              <div className="flex space-x-1 mt-1">
                {user.roles?.map(role => (
                  <span key={role.id} className="inline-flex px-2 py-0.5 text-xs font-medium bg-admin-500 text-white rounded">
                    {role.name}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Scrollable Navigation */}
      <nav className="flex-1 overflow-y-auto mt-4">
        {menuItems.map(({ path, icon: Icon, label }) => (
          <Link
            key={path}
            to={path}
            onClick={onClose}
            className={`flex items-center space-x-3 px-6 py-3 text-white hover:bg-admin-700 transition-colors duration-200 ${
              location.pathname === path ? 'bg-admin-700 border-r-4 border-admin-300' : ''
            }`}
          >
            <Icon className="h-5 w-5" />
            <span>{label}</span>
          </Link>
        ))}

        {/* Logout Button */}
        <button
          onClick={() => {
            logout();
            onClose();
          }}
          className="flex items-center space-x-3 px-6 py-3 text-white hover:bg-admin-700 transition-colors duration-200 w-full text-left"
        >
          <LogOut className="h-5 w-5" />
          <span>Logout</span>
        </button>
      </nav>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isAdmin, loading, login, logout } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-admin-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Login onLogin={login} />;
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="mb-4">
            <Shield className="h-16 w-16 text-red-500 mx-auto" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-4">You need admin privileges to access this panel.</p>
          <button
            onClick={logout}
            className="admin-btn-primary"
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

// Main App Component
const AppContent: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user } = useAuth();

  return (
    <ProtectedRoute>
      <div className="flex h-screen bg-gray-100">
        <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Header */}
          <header className="sticky top-0 z-10 bg-white shadow-sm border-b border-gray-200">
            <div className="flex items-center justify-between px-6 py-4">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden text-gray-600 hover:text-gray-900"
              >
                <Menu className="h-6 w-6" />
              </button>
              <h1 className="text-lg font-semibold text-gray-900">Train System Administration</h1>
              <div className="flex items-center space-x-4">
                {user && (
                  <span className="text-sm text-gray-600">
                    Welcome, {user.name}!
                  </span>
                )}
                <div className="text-sm text-gray-500">
                  {new Date().toLocaleDateString()}
                </div>
              </div>
            </div>
          </header>

          {/* Main content - Routes updated */}
          <main className="flex-1 overflow-x-hidden overflow-y-auto p-6">
            <Routes>
              <Route path="/regions" element={<RegionManager />} />
              <Route path="/companies" element={<CompanyManager />} />
              <Route path="/lines" element={<LinesManager />} />
              <Route path="/" element={<StationsManager />} />
              <Route path="/route-management" element={<RouteManagementManager />} />
              <Route path="/pricing" element={<PricingManagement />} />
              <Route path="/route-updates" element={<RouteUpdateManager />} />
              <Route path="/tickets" element={<TicketsManager />} />
              <Route path="/validations" element={<TicketValidationManager />} />
              <Route path="/users" element={<UsersManager />} />
              <Route path="/roles" element={<RolesManager />} />
            </Routes>
          </main>
        </div>
      </div>
    </ProtectedRoute>
  );
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppContent />
      </Router>
    </AuthProvider>
  );
}

export default App;